import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAccessToken } from '@robinpowered/dashboard-apps-common';
import { config } from 'config';
import { Sentry } from 'lib/sentry';
import { ExternalAccountType } from 'generated/graphql';

export type ReconnectOutcome = 'succeeded' | 'cancelled' | 'failed';

export type UseOAuthReconnect = {
  reconnect: (accountType: ExternalAccountType) => Promise<ReconnectOutcome>;
};

/**
 * The four scopes the calendar integration needs. This is deliberately a
 * superset of the scopes our Google SSO *login* asks for: reconnecting a
 * calendar also needs calendar write access and the contacts read used to
 * resolve invitees. Narrowing this list silently degrades the integration
 * without any visible error, so treat it as fixed unless the API team says
 * otherwise.
 */
const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/calendar',
  'https://www.googleapis.com/auth/contacts.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
];

/**
 * The Microsoft scopes for connecting one user's calendar, mirroring the
 * dashboard's `graph-service-connect` provider — the flow documented there as
 * the one that connects a Graph account to a Robin *user*, as opposed to the
 * org-level room-calendar integrations that share the same Azure app.
 *
 * The calendar/contacts/user reads come from that provider's `personal` set;
 * `offline_access` is added on top (see below) because we authorize against
 * Azure v2, where a refresh token is only issued when it's requested.
 */
const MICROSOFT_SCOPES = [
  'https://graph.microsoft.com/Contacts.Read.Shared',
  'https://graph.microsoft.com/Calendars.ReadWrite.Shared',
  'https://graph.microsoft.com/User.ReadBasic.All',
  'https://graph.microsoft.com/User.Read',
  // Azure AD v2 is strictly scope-based and only returns a refresh token when
  // `offline_access` is requested explicitly. The legacy v1 flow got one for
  // free; retargeting to v2 (for the graph-service subject) means we have to
  // ask. Without it a reconnect succeeds, then dies within the hour when the
  // access token expires — the same failure `access_type=offline` prevents for
  // Google.
  'offline_access',
];

const GOOGLE_AUTHORIZE_URL = 'https://accounts.google.com/o/oauth2/auth';

/**
 * Azure AD **v2**. The older v1 endpoint (`login.windows.net`) takes a
 * `resource` instead of `scope`, and its redirect lands on a different
 * dashboard callback that writes a `microsoft_365` account. Our health probe
 * asks about `MICROSOFT_GRAPH`, so a v1 reconnect would succeed and leave the
 * row on screen forever, because it wrote a record nothing here reads.
 */
const MICROSOFT_AUTHORIZE_URL =
  'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';

/**
 * OAuth `state` marker identifying this as the in-place reconnect flow. The
 * dashboard's OAuth callback (`resolveRedirect`) reads it and stays passive —
 * it must NOT run its own deeplink token exchange, which would spend the
 * one-time code before we poll and post it (the code then fails with
 * `invalid_grant`). Keep this string in sync with the dashboard check.
 */
const RECONNECT_STATE = 'robin-reconnect';

/** Roomy enough for provider consent screens without covering the dashboard. */
const POPUP_FEATURES = 'width=600,height=700';

const POLL_INTERVAL_MS = 400;

/**
 * How long we watch the popup before giving up. Five minutes covers a slow
 * sign-in — typing a password, an MFA push to a phone, picking between work
 * accounts — while still guaranteeing that a popup the user wandered away from
 * cannot leave us polling for the rest of the session.
 */
const FLOW_TIMEOUT_MS = 5 * 60 * 1000;

type ProviderRequest = {
  authorizeUrl: string;
  /** Sent again during the exchange; the provider requires an exact match. */
  redirectUri: string;
  exchangeUrl: string;
};

/**
 * Turns an account type into everything the flow needs: where to send the user,
 * and where to redeem the code afterwards. Returns null when the provider's
 * client ID is not configured for this deployment, which is the one case where
 * we must not open a popup at all — the provider would only show an error page.
 *
 * The two providers agree on almost nothing (different parameter sets,
 * different API versions, different exchange endpoints); keeping that
 * disagreement in this one function is what lets callers just name an account
 * type.
 */
const describeProvider = (
  accountType: ExternalAccountType
): ProviderRequest | null => {
  const { origin } = window.location;

  if (accountType === ExternalAccountType.Google) {
    const clientId = config.googleClientId;
    if (!clientId) {
      return null;
    }

    const redirectUri = `${origin}/api/oauth/google`;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: GOOGLE_SCOPES.join(' '),
      // Force the consent screen: a reconnect exists precisely because the
      // previous grant is no longer good, so a silent re-auth would hand back
      // the same broken state.
      prompt: 'consent',
      // Without `offline`, Google issues an access token and no refresh token,
      // so the integration would break again within the hour — a reconnect
      // that reports success and silently expires over lunch. The legacy
      // Angular flow sends it (`accessType: 'offline'` in the dashboard's
      // integrations constants); so must we.
      access_type: 'offline',
      // `prompt=consent` re-issues the grant with exactly the scopes named
      // above. If the original connection was granted anything wider, that
      // would silently narrow it; this preserves what the user already
      // approved. Also mirrors the legacy flow (`includeGrantedScopes: true`).
      include_granted_scopes: 'true',
      // Marks this as the in-place reconnect so the dashboard callback stays
      // passive and lets us exchange the code (see RECONNECT_STATE).
      state: RECONNECT_STATE,
    });

    return {
      authorizeUrl: `${GOOGLE_AUTHORIZE_URL}?${params.toString()}`,
      redirectUri,
      exchangeUrl: '/api/oauth/google/user',
    };
  }

  const clientId = config.microsoftClientId;
  if (!clientId) {
    return null;
  }

  const redirectUri = `${origin}/api/oauth/graph-service`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: MICROSOFT_SCOPES.join(' '),
    // Let the user choose the account, since the one they are reconnecting may
    // not be the one Microsoft remembers on this device.
    prompt: 'select_account',
    // Marks this as the in-place reconnect so the dashboard callback stays
    // passive and lets us exchange the code (see RECONNECT_STATE).
    state: RECONNECT_STATE,
  });

  return {
    authorizeUrl: `${MICROSOFT_AUTHORIZE_URL}?${params.toString()}`,
    redirectUri,
    exchangeUrl: '/api/users/external-accounts/microsoft_service',
  };
};

type PopupResult =
  | { status: 'code'; code: string }
  /** The user closed the popup — an ordinary change of mind, not a fault. */
  | { status: 'cancelled' }
  /** The provider sent us back with an `error` instead of a code. */
  | { status: 'denied' }
  | { status: 'timedOut' }
  /** Our host disappeared while the popup was still open. */
  | { status: 'abandoned' };

/**
 * Reads the popup's query string once it is back on our own origin.
 *
 * For most of this flow the popup sits on accounts.google.com or
 * login.windows.net, and touching `popup.location` from here raises a
 * SecurityError. That throw is the *expected* state, not a failure, so it maps
 * to "nothing to read yet" and polling continues.
 */
const readRedirectParams = (popup: Window): URLSearchParams | null => {
  try {
    const { origin, search } = popup.location;
    if (origin !== window.location.origin || !search) {
      return null;
    }
    return new URLSearchParams(search);
  } catch {
    return null;
  }
};

/**
 * Watches a popup until it comes back to us with an authorization code, the
 * user closes it, or we run out of patience. Every exit path clears both
 * timers, so no interval outlives the flow.
 */
const waitForRedirect = (
  popup: Window,
  isMounted: () => boolean
): Promise<PopupResult> =>
  new Promise<PopupResult>((resolve) => {
    let intervalId = 0;
    let timeoutId = 0;
    let settled = false;

    const finish = (result: PopupResult): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
      resolve(result);
    };

    const poll = (): void => {
      if (!isMounted()) {
        finish({ status: 'abandoned' });
        return;
      }

      if (popup.closed) {
        finish({ status: 'cancelled' });
        return;
      }

      const params = readRedirectParams(popup);
      if (!params) {
        return;
      }

      if (params.get('error')) {
        finish({ status: 'denied' });
        return;
      }

      const code = params.get('code');
      if (code) {
        finish({ status: 'code', code });
      }
    };

    intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
    timeoutId = window.setTimeout(
      () => finish({ status: 'timedOut' }),
      FLOW_TIMEOUT_MS
    );
  });

/**
 * URL-decodes a cookie value without throwing. `decodeURIComponent` raises a
 * `URIError` on a malformed percent-sequence; a broken CSRF cookie shouldn't
 * take down the exchange, so fall back to the raw value (which still works when
 * the cookie wasn't actually encoded).
 */
const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed CSRF cookie is unexpected and would otherwise fail the
    // exchange silently; surface it (without the value, which is a token).
    Sentry.captureException(new Error('Malformed XSRF-TOKEN cookie'), {
      tags: { feature: 'reconnect-accounts' },
    });
    return value;
  }
};

/**
 * Builds the exchange request headers.
 *
 * The dashboard authenticates and protects these POST routes two ways, and a
 * bare `fetch` satisfies neither on its own (the dashboard's own Angular app
 * does both automatically via `$http`):
 *
 * 1. **CSRF** — `csurf` sets a readable `XSRF-TOKEN` cookie the caller must echo
 *    back in a header, or the request is rejected with `invalid csrf token`.
 * 2. **Auth** — the exchange controller builds its Robin SDK instance from the
 *    `Authorization` header (not the session cookie), so without a bearer token
 *    it throws `Access token required to create an instance of robin SDK`.
 *
 * A missing cookie or token just omits that header; the exchange then fails the
 * same way it did before rather than throwing here.
 */
const buildExchangeHeaders = (
  accessToken: string | null
): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const xsrf = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  if (xsrf) {
    headers['X-XSRF-TOKEN'] = safeDecode(xsrf[1]);
  }
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
};

/**
 * Trades the authorization code for a reconnected account on the dashboard
 * backend. Any non-2xx answer, or a network error, means the account is still
 * broken.
 */
const exchangeCode = async (
  request: ProviderRequest,
  code: string,
  accessToken: string | null
): Promise<boolean> => {
  try {
    const response = await fetch(request.exchangeUrl, {
      method: 'POST',
      // Same-origin so the dashboard session cookie rides along; the bearer
      // token and CSRF header are added in buildExchangeHeaders.
      credentials: 'same-origin',
      headers: buildExchangeHeaders(accessToken),
      body: JSON.stringify({ code, redirectUri: request.redirectUri }),
    });

    // A non-2xx means the backend rejected the exchange — a bad secret, a 500,
    // a failed reconnect transaction. That's a deployment/system fault, not a
    // user choice, so it must be visible rather than silently reported as a
    // failed row.
    if (!response.ok) {
      Sentry.captureException(
        new Error(`Reconnect token exchange failed (${response.status})`),
        {
          tags: { feature: 'reconnect-accounts' },
          extra: { exchangeUrl: request.exchangeUrl, status: response.status },
        }
      );
    }
    return response.ok;
  } catch (error) {
    // Network error / JSON failure — also a real fault worth surfacing, not a
    // user cancellation (that path never reaches here; it's handled by the
    // popup closing).
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        tags: { feature: 'reconnect-accounts' },
        extra: { exchangeUrl: request.exchangeUrl },
      }
    );
    return false;
  }
};

/**
 * Never settles. Used when our host unmounted mid-flow: resolving would run
 * the caller's continuation against UI that no longer exists.
 */
const abandoned = <T>(): Promise<T> => new Promise<T>(() => undefined);

/**
 * Reconnects a single calendar provider without leaving the current page.
 *
 * IMPORTANT: `reconnect` must be called straight from a user gesture (a click
 * handler). It opens a popup, and every major browser blocks popups that are
 * not attributable to a gesture — this was confirmed live on this feature. Do
 * not "helpfully" call it from an effect, a timer, or after an await that
 * yields to the network; the popup will silently never appear and the user will
 * see an unexplained failure.
 */
export const useOAuthReconnect = (): UseOAuthReconnect => {
  const isMountedRef = useRef(true);
  // The exchange endpoint authenticates from the Authorization header, so we
  // send the same token the shell reads from the session cookie.
  const { accessToken } = useAccessToken(config.sessionCookieName);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const reconnect = useCallback(
    async (accountType: ExternalAccountType): Promise<ReconnectOutcome> => {
      const request = describeProvider(accountType);
      if (!request) {
        // The provider has no client ID configured, so no popup can open and
        // the user just sees a generic error. Report it: this is the one
        // failure here that is a deployment problem rather than anything the
        // user did, and it would otherwise be invisible.
        Sentry.captureException(
          new Error(`No OAuth client ID configured for ${accountType}`),
          {
            tags: { feature: 'reconnect-accounts' },
            extra: { accountType },
          }
        );
        return 'failed';
      }

      // The exchange authenticates from this token, so without it the reconnect
      // can only fail after the user has already gone through the OAuth popup.
      // Bail out first — surface it (a resolved auth is normally present by the
      // time the reconnect entry point is reachable, so this is unexpected).
      if (!accessToken) {
        Sentry.captureException(
          new Error(`No access token available to reconnect ${accountType}`),
          {
            tags: { feature: 'reconnect-accounts' },
            extra: { accountType },
          }
        );
        return 'failed';
      }

      // The window name is per-provider, never shared. With one constant name,
      // starting a second provider's reconnect would re-navigate the first
      // provider's popup — and the first poller, still watching that same
      // window, would read the second provider's `code` and post it to the
      // first provider's exchange endpoint.
      const popup = window.open(
        request.authorizeUrl,
        `robin-reconnect-${accountType.toLowerCase()}`,
        POPUP_FEATURES
      );
      if (!popup) {
        return 'failed';
      }

      const result = await waitForRedirect(popup, () => isMountedRef.current);

      // Once we stop watching the popup it can tell us nothing more, so it goes
      // away on every path — including the ones we are about to call failures.
      if (!popup.closed) {
        popup.close();
      }

      if (result.status === 'abandoned') {
        return abandoned<ReconnectOutcome>();
      }

      // Closing the popup is a decision, not a malfunction, so the caller can
      // stay quiet about it instead of showing an error.
      if (result.status === 'cancelled') {
        return 'cancelled';
      }

      if (result.status !== 'code') {
        return 'failed';
      }

      const exchanged = await exchangeCode(request, result.code, accessToken);

      if (!isMountedRef.current) {
        return abandoned<ReconnectOutcome>();
      }

      return exchanged ? 'succeeded' : 'failed';
    },
    [accessToken]
  );

  return useMemo(() => ({ reconnect }), [reconnect]);
};
